import type { NuxtApp } from '#app'
import { ApiError, isRateLimitedError } from '~/composables/useApi'
import type { OnlineStore } from '~/composables/useOnlineStores'
import {
  isRevokedStorefrontLabel,
  type StorefrontHost,
} from '~/composables/useStorefrontHost'

const HOME_SECTIONS_FIRST_PAGE = 1
const HOME_SECTIONS_PER_PAGE = 3

function seedPublicStorePayload(
  payload: Record<string, unknown>,
  store: Pick<OnlineStore, 'id' | 'homeSections' | 'homeSectionsMeta'>,
) {
  payload[`public-online-store:${store.id}`] = store
  const sections = Array.isArray(store.homeSections) ? store.homeSections : []
  const per = HOME_SECTIONS_PER_PAGE
  payload[`public-online-store:${store.id}:hs:${HOME_SECTIONS_FIRST_PAGE}:${per}`] = {
    ...store,
    homeSections: sections.slice(0, per),
    homeSectionsMeta: store.homeSectionsMeta ?? {
      currentPage: HOME_SECTIONS_FIRST_PAGE,
      perPage: per,
      total: sections.length,
      hasMore: sections.length > per,
    },
  }
}

type HostLookup = {
  host: StorefrontHost
  store: Pick<OnlineStore, 'id' | 'homeSections' | 'homeSectionsMeta'> | null
}

/**
 * Ask the public API which store this host is. Does not write `useState` —
 * the caller decides when the result may paint, so a client confirm cannot
 * land in the middle of hydration.
 */
async function lookupStorefrontHost(
  nuxtApp: NuxtApp,
  subdomain: string,
): Promise<HostLookup> {
  const { useOnlineStores } = await import('~/composables/useOnlineStores')
  let fetchPublicBySubdomain: ((label: string) => Promise<OnlineStore | null>) | null = null
  nuxtApp.runWithContext(() => {
    fetchPublicBySubdomain = useOnlineStores().fetchPublicBySubdomain
  })

  const empty = (patch: Partial<StorefrontHost>): HostLookup => ({
    store: null,
    host: {
      subdomain,
      storeId: null,
      unpublished: false,
      missing: false,
      verifying: false,
      rateLimited: false,
      ...patch,
    },
  })

  try {
    const lookup = fetchPublicBySubdomain
    const store = lookup
      ? await nuxtApp.runWithContext(() => lookup(subdomain))
      : null
    return {
      store,
      host: {
        subdomain,
        storeId: store?.id ?? null,
        unpublished: false,
        // Null = unknown / revoked label; same guest-facing "not found".
        // An SSR miss stays verifying until the client repeats the lookup.
        missing: !store,
        verifying: import.meta.server && !store,
        rateLimited: false,
      },
    }
  }
  catch (error) {
    // The owner is still designing this one; the storefront says so rather
    // than pretending the address is wrong. SSR "unpublished" is re-checked
    // on the client (publish can land between the two requests).
    if (error instanceof ApiError && error.code === 'storeNotPublished') {
      return empty({
        unpublished: true,
        verifying: import.meta.server,
      })
    }

    if (isRateLimitedError(error)) {
      return empty({ rateLimited: true })
    }

    if (error instanceof ApiError && error.status === 404) {
      return empty({
        missing: true,
        // SSR 404 is provisional. The client confirms before "not found" paints.
        verifying: import.meta.server,
      })
    }

    // Transport / 5xx: SSR keeps verifying so the client retries and paints
    // loading instead of "not found". A failed client pass stops verifying
    // (confirmed miss) so the page is not stuck on the boot cover forever.
    return empty({
      missing: import.meta.client,
      verifying: import.meta.server,
    })
  }
}

/**
 * Trade the storefront host for the store it belongs to, once per visit.
 *
 * Runs before any page so `/` on `my-store.vntru.com` already knows
 * which store it is rendering. The result travels to the client in the SSR
 * payload, so this costs one request per visit, not per navigation.
 *
 * Inactive and domain-revoked both end as {@link StorefrontHost.missing}:
 * - inactive keeps the subdomain but the public API 404s
 * - domain-revoked frees the old label, so the lookup finds nothing (also 404)
 */
export default defineNuxtPlugin({
  name: 'storefront-host',
  async setup(nuxtApp) {
  const host = useStorefrontHost()
  const subdomain = currentStoreSubdomain()

  if (!subdomain) return

  // Parked placeholder after domain revocation — never a shoppable storefront.
  if (isRevokedStorefrontLabel(subdomain)) {
    host.value = { subdomain, storeId: null, unpublished: false, missing: true, verifying: false, rateLimited: false }
    return
  }

  // Hydrating: trust a finished SSR success for this exact host. Retry SSR
  // "missing" / "unpublished" on the client — Windows Node cannot resolve
  // `{shop}.localhost`, and open-live-right-after-publish can still see the
  // draft for one request. Pages treat `verifying` as loading, not not-found.
  const already = host.value
  if (
    already.subdomain === subdomain
    && (already.storeId || already.rateLimited)
  ) {
    return
  }

  // Client already confirmed this host is gone. Do not look it up again.
  if (import.meta.client && already.subdomain === subdomain && already.missing && !already.verifying) {
    return
  }

  function commit(result: HostLookup) {
    host.value = result.host
    if (result.store) {
      // Homepage reads the paged key; seed both so hydrate does not flash
      // "not found" while a second by-id request is still in flight.
      seedPublicStorePayload(nuxtApp.payload.data, result.store)
    }
  }

  // SSR left this host provisional (`verifying`). Nuxt awaits this plugin
  // before the first client render, so writing the confirm here swapped the
  // loading cover for "not found" (or the live shop) and Vue logged
  // "Hydration completed but contains mismatches." Fetch now, paint after
  // the SSR tree has hydrated. `app:mounted` is too early — it runs before
  // an async page's Suspense finishes hydrating.
  if (import.meta.client && already.subdomain === subdomain && already.verifying) {
    const pending = lookupStorefrontHost(nuxtApp, subdomain)
    nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
      void pending.then(commit)
    })
    return
  }

  host.value = {
    subdomain,
    storeId: null,
    unpublished: false,
    missing: false,
    // Keep the loading cover up while the first lookup is in flight.
    verifying: true,
    rateLimited: false,
  }

  commit(await lookupStorefrontHost(nuxtApp, subdomain))
  },
})
